export const CANTONS = [
  ["AG", "Argovia"], ["AI", "Appenzello Interno"], ["AR", "Appenzello Esterno"],
  ["BE", "Berna"], ["BL", "Basilea Campagna"], ["BS", "Basilea Città"],
  ["FR", "Friburgo"], ["GE", "Ginevra"], ["GL", "Glarona"], ["GR", "Grigioni"],
  ["JU", "Giura"], ["LU", "Lucerna"], ["NE", "Neuchâtel"], ["NW", "Nidvaldo"],
  ["OW", "Obvaldo"], ["SG", "San Gallo"], ["SH", "Sciaffusa"], ["SO", "Soletta"],
  ["SZ", "Svitto"], ["TG", "Turgovia"], ["TI", "Ticino"], ["UR", "Uri"],
  ["VD", "Vaud"], ["VS", "Vallese"], ["ZG", "Zugo"], ["ZH", "Zurigo"],
];

export const MODEL_LABELS = {
  BASE: "Standard",
  STANDARD: "Standard",
  FLEX: "FLEX",
  PRAXIS: "Medico / rete",
  TEL_DIG: "Telemedicina / digitale",
  PHARM: "Farmacia",
  ALTRO: "Altro modello",
};

export const ENVIRONMENTAL_REFUND_MONTHLY = { 2026: 5.15, 2027: 4.75 };

const productKey = (row) => `${row[0]}|${row[6]}`;
const normalizePlanText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');
const tariffAliases = (tariff) => [
  tariff,
  String(tariff).replace(/\([^)]*\)/g, ''),
  ...[...String(tariff).matchAll(/\(([^)]*)\)/g)].map((match) => match[1]),
].map(normalizePlanText).filter(Boolean);

const oldModel = (model) => ({ STANDARD: 'BASE', BASE: 'BASE' }[model] || model);
const KNOWN_TARIFF_RENAMES = new Map([
  ['1562|BFP_CAF', '1562|BFP_CPF'], // Helsana BeneFit PLUS Flexmed
]);

// The 2027 category is assigned by the FOPH. Product names such as
// "casamed pharm" do not reliably identify that category.
const matchProducts = (rows2026, rows2027) => {
  const oldProducts = new Map(rows2026.map((row) => [productKey(row), row]));
  const newProducts = new Map(rows2027.map((row) => [productKey(row), row]));
  const matches = new Map();
  const used = new Set();
  const connect = (oldKey, newKey) => { matches.set(oldKey, newProducts.get(newKey)); used.add(newKey); };

  for (const key of oldProducts.keys()) if (newProducts.has(key)) connect(key, key);
  for (const [oldKey, newKey] of KNOWN_TARIFF_RENAMES) {
    if (oldProducts.has(oldKey) && newProducts.has(newKey) && !matches.has(oldKey) && !used.has(newKey)) connect(oldKey, newKey);
  }

  const pairUnique = (tokens) => {
    const oldByToken = new Map();
    const newByToken = new Map();
    for (const [key, row] of oldProducts) {
      if (matches.has(key)) continue;
      for (const token of new Set(tokens(row))) oldByToken.set(token, [...(oldByToken.get(token) || []), key]);
    }
    for (const [key, row] of newProducts) {
      if (used.has(key)) continue;
      for (const token of new Set(tokens(row))) newByToken.set(token, [...(newByToken.get(token) || []), key]);
    }
    for (const [token, oldKeys] of oldByToken) {
      const newKeys = newByToken.get(token);
      if (oldKeys.length === 1 && newKeys?.length === 1 && !matches.has(oldKeys[0]) && !used.has(newKeys[0])) connect(oldKeys[0], newKeys[0]);
    }
  };

  pairUnique((row) => tariffAliases(row[6]).map((alias) => `${row[0]}|${alias}`));
  pairUnique((row) => {
    const label = normalizePlanText(row[9]);
    return label ? [`${row[0]}|${label}`] : [];
  });
  return matches;
};

export const harmonizePremiumModels = (rows2026, rows2027) => {
  const current = (rows2027 || []).map((row) => [...row]);
  const matches = matchProducts(rows2026 || [], current);
  const previous = (rows2026 || []).map((row) => {
    const copy = [...row];
    const counterpart = matches.get(productKey(row));
    copy[6] = counterpart?.[6] || row[6];
    copy[7] = counterpart?.[7] || oldModel(row[7]);
    return copy;
  });
  return [previous, current];
};

const normalizeSearch = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

export const searchLocations = (locations, query, limit = 5) => {
  const needle = normalizeSearch(query);
  if (needle.length < 2) return [];
  const terms = needle.split(/\s+/).filter(Boolean);
  return (locations || [])
    .map((location) => {
      const zip = String(location.z);
      const locality = normalizeSearch(location.l);
      const municipality = normalizeSearch(location.n);
      const haystack = `${zip} ${locality} ${municipality} ${normalizeSearch(location.c)}`;
      if (!terms.every((term) => haystack.includes(term))) return null;
      let score = 0;
      if (zip === needle) score += 1000;
      else if (zip.startsWith(needle)) score += 700;
      if (locality === needle) score += 900;
      else if (locality.startsWith(needle)) score += 600;
      if (municipality === needle) score += 800;
      else if (municipality.startsWith(needle)) score += 500;
      terms.forEach((term) => {
        if (locality.startsWith(term)) score += 80;
        if (municipality.startsWith(term)) score += 60;
      });
      return { location, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || String(a.location.z).localeCompare(String(b.location.z)) || a.location.l.localeCompare(b.location.l, 'it'))
    .slice(0, limit)
    .map(({ location }) => location);
};

export const classifyAge = (birthYear, premiumYear) => {
  const birth = Number(birthYear);
  const year = Number(premiumYear);
  if (!Number.isInteger(birth) || birth > year) return null;
  const age = year - birth;
  if (age <= 18) return "K";
  if (age <= 25) return "J";
  return "E";
};

export const franchisesFor = (birthYear, premiumYear) => (
  classifyAge(birthYear, premiumYear) === "K"
    ? [0, 100, 200, 300, 400, 500, 600]
    : [300, 500, 1000, 1500, 2000, 2500]
);

export const familyProfilesForYear = (people, premiumYear, declaredChildCount = 0) => {
  const children = (people || [])
    .map((person, index) => ({ person, index }))
    .filter(({ person }) => classifyAge(person.birthYear, premiumYear) === 'K')
    .sort((a, b) => Number(a.person.birthYear) - Number(b.person.birthYear) || a.index - b.index);
  const orderById = new Map(children.map(({ person }, index) => [person.id, Number(person.childOrder) || index + 1]));
  const familyChildCount = Math.max(Number(declaredChildCount) || 0, children.length, ...orderById.values(), 1);
  return (people || []).map((person) => ({
    ...person,
    childOrder: orderById.get(person.id) || 1,
    familyChildCount,
  }));
};

export const getRegion = (regions, year, canton, municipalityBfs) => {
  const rows = regions?.[String(year)] || [];
  const match = rows.find((row) => row.c === canton && String(row.b) === String(municipalityBfs));
  return match?.r || 0;
};

export const getLocationRegion = (regions, year, location, locations) => {
  const direct = getRegion(regions, year, location.c, location.b);
  if (direct) return direct;
  const samePlace = (locations || []).find((candidate) =>
    candidate.c === location.c
    && candidate.z === location.z
    && candidate.l === location.l
    && getRegion(regions, year, candidate.c, candidate.b));
  return samePlace ? getRegion(regions, year, samePlace.c, samePlace.b) : 0;
};

export const municipalitiesFor = (regions, canton) => {
  const seen = new Map();
  [2026, 2027].forEach((year) => {
    (regions?.[String(year)] || [])
      .filter((row) => row.c === canton)
      .forEach((row) => seen.set(String(row.b), row));
  });
  return [...seen.values()].sort((a, b) => a.n.localeCompare(b.n, "it"));
};

export const filterPremiums = (rows, profile, premiumYear, region) => {
  const age = classifyAge(profile.birthYear, premiumYear);
  const candidates = (rows || []).filter((row) => {
    const [, rowRegion, rowAge, , accident, franchise, , model] = row;
    const modelMatches = !profile.models?.length || profile.models.includes(model);
    return Number(rowRegion) === Number(region)
      && rowAge === age
      && Boolean(accident) === Boolean(profile.accident)
      && Number(franchise) === Number(profile.franchise)
      && modelMatches;
  });
  if (age !== 'K') return candidates;
  const childOrder = Math.min(5, Math.max(1, Number(profile.childOrder) || (profile.thirdChild ? 3 : 1)));
  const familyChildCount = Math.min(5, Math.max(childOrder, Number(profile.familyChildCount) || 1));
  const available = new Map();
  for (const row of candidates) {
    const key = productKey(row);
    available.set(key, [...(available.get(key) || []), row]);
  }
  return [...available.values()].map((productRows) => {
    const bySubgroup = new Map(productRows.map((row) => [row[3], row]));
    // Priminfo applies K4 to all children from a two-child household and K5
    // to all children from a three-child household. K3 applies from child 3.
    if (familyChildCount >= 3 && bySubgroup.has('K5')) return bySubgroup.get('K5');
    if (familyChildCount >= 2 && bySubgroup.has('K4')) return bySubgroup.get('K4');
    if (childOrder >= 3 && bySubgroup.has('K3')) return bySubgroup.get('K3');
    return bySubgroup.get('K1');
  }).filter(Boolean);
};

export const buildComparisons = (rows2026, rows2027, insurers) => {
  const merged = new Map();
  rows2027.forEach((row) => {
    const key = productKey(row);
    if (merged.has(key)) throw new Error(`Duplicate premium for ${key} in 2027`);
    merged.set(key, { ...(merged.get(key) || {}), row2027: row });
  });
  rows2026.forEach((row) => {
    const key = productKey(row);
    if (merged.get(key)?.row2026) throw new Error(`Duplicate premium for ${key} in 2026`);
    merged.set(key, { ...(merged.get(key) || {}), row2026: row });
  });

  return [...merged.values()].map(({ row2026, row2027 }) => {
    const preferred = row2027 || row2026;
    const grossPremium2026 = row2026?.[8] ?? null;
    const grossPremium2027 = row2027?.[8] ?? null;
    const premium2026 = grossPremium2026 == null ? null : Math.round((grossPremium2026 - ENVIRONMENTAL_REFUND_MONTHLY[2026]) * 100) / 100;
    const premium2027 = grossPremium2027 == null ? null : Math.round((grossPremium2027 - ENVIRONMENTAL_REFUND_MONTHLY[2027]) * 100) / 100;
    return {
      key: productKey(preferred),
      insurerCode: preferred[0],
      insurer: insurers?.[preferred[0]] || `Assicuratore ${preferred[0]}`,
      tariff: preferred[6],
      model: preferred[7],
      label: preferred[9],
      familyDiscount: [row2026?.[3], row2027?.[3]].some((subgroup) => ['K3', 'K4', 'K5'].includes(subgroup)),
      premium2026,
      premium2027,
      grossPremium2026,
      grossPremium2027,
      delta: premium2026 != null && premium2027 != null ? premium2027 - premium2026 : null,
    };
  }).sort((a, b) => {
    if (a.premium2027 == null && b.premium2027 != null) return 1;
    if (a.premium2027 != null && b.premium2027 == null) return -1;
    return (a.premium2027 ?? a.premium2026 ?? Infinity) - (b.premium2027 ?? b.premium2026 ?? Infinity);
  });
};

export const minimumFamilyPremiums = (peopleResults) => {
  const minimums = { 2026: null, 2027: null };
  if (!peopleResults?.length) return minimums;
  for (const year of [2026, 2027]) {
    const premiumsByInsurer = peopleResults.map(({ offers }) => {
      const byInsurer = new Map();
      for (const offer of offers) {
        const premium = offer[`premium${year}`];
        if (premium != null) byInsurer.set(offer.insurerCode, Math.min(byInsurer.get(offer.insurerCode) ?? Infinity, premium));
      }
      return byInsurer;
    });
    for (const [insurer, firstPremium] of premiumsByInsurer[0]) {
      const premiums = [firstPremium, ...premiumsByInsurer.slice(1).map((byInsurer) => byInsurer.get(insurer))];
      if (premiums.some((premium) => premium == null)) continue;
      const total = Math.round(premiums.reduce((sum, premium) => sum + premium, 0) * 100) / 100;
      minimums[year] = Math.min(minimums[year] ?? Infinity, total);
    }
  }
  return minimums;
};

export const sortOffersWithFavorites = (offers, favoriteKeys, personId) => {
  const favorites = new Set(favoriteKeys || []);
  return [...(offers || [])].sort((a, b) => {
    const aFavorite = favorites.has(`${personId}::${a.key}`);
    const bFavorite = favorites.has(`${personId}::${b.key}`);
    if (aFavorite !== bFavorite) return aFavorite ? -1 : 1;
    return (a.premium2027 ?? a.premium2026 ?? Infinity) - (b.premium2027 ?? b.premium2026 ?? Infinity);
  });
};

export const formatConsultationMessage = ({ location, peopleResults, favoriteKeys }) => {
  const favorites = new Set(favoriteKeys || []);
  const lines = [
    'Richiesta consulenza premi LAMal 2026–2027',
    `Domicilio: ${location.z} ${location.l} — Comune ${location.n} (${location.c})`,
    '',
  ];
  peopleResults.forEach(({ person, offers }, index) => {
    lines.push(`${person.name || `Persona ${index + 1}`} — nascita ${person.birthYear}, franchigia CHF ${person.franchise}, infortuni ${person.accident ? 'inclusi' : 'esclusi'}`);
    offers.filter((offer) => favorites.has(`${person.id}::${offer.key}`)).forEach((offer) => {
      lines.push(`• ${offer.insurer} — ${offer.label} — 2026 ${formatChf(offer.premium2026)}/mese — 2027 ${formatChf(offer.premium2027)}/mese${offer.familyDiscount ? ' (sconto famiglia: figli presso la stessa cassa)' : ''}`);
    });
    lines.push('');
  });
  return lines.join('\n').trim();
};

export const formatChf = (value, digits = 2) => value == null
  ? "—"
  : new Intl.NumberFormat("it-CH", {
      style: "currency", currency: "CHF", minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(value);
